import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Phone,
  Sparkle,
  TrendDown,
  TrendUp,
  Star,
  Fire,
  ArrowRight,
  CheckCircle,
  Spinner,
  Warning,
  CaretDown,
} from "@phosphor-icons/react";

/**
 * V4.17 — Pilotage intelligent des apporteurs (5 blocs actionnables).
 *
 * Doctrine : "Quel apporteur dois-je contacter MAINTENANT pour générer
 * plus de dossiers dans les prochaines semaines ?"
 *
 * Ordre (validé user) :
 *   1. À appeler cette semaine (HERO, dominant rouge/orange)
 *   2. Opportunités
 *   3. En perte de vitesse
 *   4. En croissance
 *   5. Top potentiel
 *
 * Chaque item expose deux actions : "Voir la fiche" + "Noter un contact".
 * Le composant accepte un `searchQuery` parent pour filtrer en cohérence
 * avec la recherche globale V4.16.
 */

const _norm = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const PRIORITY_META = {
  high: { icon: Fire, label: "Priorité haute", color: "var(--cf-danger)", bg: "var(--cf-danger-soft)", glyph: "🔥" },
  medium: { icon: Warning, label: "Priorité moyenne", color: "var(--cf-warning)", bg: "var(--cf-warning-soft)", glyph: "🟠" },
  low: { icon: Star, label: "Priorité faible", color: "var(--cf-text-3)", bg: "var(--cf-bg-2)", glyph: "⚪" },
};

// ---------- Boutons d'action ----------
function ActionButtons({ item, onOpen, onContact, contactingKey }) {
  const isContacting = contactingKey === item.name_key;
  return (
    <div className="flex items-center gap-1.5 flex-shrink-0">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onContact(item);
        }}
        disabled={isContacting}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-[color:var(--cf-success-soft)] text-[color:var(--cf-success-text)] text-[10.5px] font-extrabold hover:brightness-95 disabled:opacity-60 transition-all"
        data-testid={`insight-mark-contact-${item.name_key}`}
        title="Noter un contact (today)"
      >
        {isContacting ? (
          <Spinner size={11} weight="bold" className="animate-spin" />
        ) : (
          <Phone size={11} weight="fill" />
        )}
        <span className="hidden sm:inline">Noter</span>
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpen(item);
        }}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-[color:var(--cf-primary-soft)] text-[color:var(--cf-primary-hover)] text-[10.5px] font-extrabold hover:brightness-95 transition-all"
        data-testid={`insight-open-${item.name_key}`}
      >
        <span className="hidden sm:inline">Fiche</span>
        <ArrowRight size={11} weight="bold" />
      </button>
    </div>
  );
}

// ---------- Bloc 1 : À appeler (HERO) ----------
function BlockACalled({ items, onOpen, onContact, contactingKey, expanded, onToggle }) {
  if (!items.length) return null;
  const display = expanded ? items : items.slice(0, 3);
  return (
    <section
      className="rounded-2xl p-4 mb-3 border border-[color:var(--cf-danger-border)]"
      style={{ background: "linear-gradient(135deg, var(--cf-danger-soft) 0%, var(--cf-warning-soft) 100%)" }}
      data-testid="insight-block-a-appeler"
    >
      <div className="flex items-center gap-2 mb-3">
        <Fire size={18} weight="fill" color="var(--cf-danger)" />
        <h3 className="font-display text-base md:text-lg font-extrabold text-[color:var(--cf-danger-text)]">
          À appeler cette semaine
        </h3>
        <span
          className="ml-auto px-2 py-0.5 rounded-full bg-[color:var(--cf-danger)] text-white text-[11px] font-extrabold tabular-nums"
          data-testid="insight-a-appeler-count"
        >
          {items.length}
        </span>
      </div>

      <ul className="space-y-2">
        {display.map((it) => {
          const meta = PRIORITY_META[it.priority] || PRIORITY_META.low;
          return (
            <li
              key={it.name_key}
              onClick={() => onOpen(it)}
              className="cursor-pointer rounded-xl p-3 bg-white/85 backdrop-blur flex items-start gap-3 hover:bg-white transition-colors"
              data-testid={`insight-a-appeler-row-${it.name_key}`}
            >
              {/* Pastille priorité */}
              <div
                className="w-10 h-10 rounded-xl flex flex-col items-center justify-center flex-shrink-0"
                style={{ background: meta.bg, color: meta.color }}
                title={meta.label}
              >
                <meta.icon size={16} weight="fill" />
                <span className="text-[8.5px] font-extrabold tabular-nums mt-0.5">{it.priority_score}</span>
              </div>

              {/* Nom + raisons */}
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[14px] text-slate-900 truncate">{it.name}</div>
                <div className="text-[11px] text-slate-500 font-semibold leading-tight mt-0.5">
                  {it.reasons.join(" · ")}
                </div>
              </div>

              <ActionButtons item={it} onOpen={onOpen} onContact={onContact} contactingKey={contactingKey} />
            </li>
          );
        })}
      </ul>

      {items.length > 3 && (
        <button
          type="button"
          onClick={onToggle}
          className="mt-2 text-[11.5px] font-bold text-[color:var(--cf-danger-text)] hover:text-[color:var(--cf-danger-text)] flex items-center gap-1"
          data-testid="insight-a-appeler-toggle"
        >
          <CaretDown size={11} weight="bold" className={expanded ? "rotate-180" : ""} />
          {expanded ? "Réduire" : `Voir les ${items.length - 3} autres`}
        </button>
      )}
    </section>
  );
}

// ---------- Bloc compact réutilisable (2/3/4/5) ----------
function CompactBlock({
  icon: Icon,
  iconColor,
  iconBg,
  title,
  items,
  emptyHint,
  renderItemMeta,
  onOpen,
  onContact,
  contactingKey,
  expanded,
  onToggle,
  testid,
}) {
  if (!items.length) return null;
  const display = expanded ? items : items.slice(0, 3);
  return (
    <section className="card-base p-4 mb-3" data-testid={`insight-block-${testid}`}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: iconBg }}>
          <Icon size={14} weight="fill" color={iconColor} />
        </div>
        <h3 className="font-display text-[14px] md:text-base font-extrabold text-slate-900">{title}</h3>
        <span
          className="ml-auto px-2 py-0.5 rounded-full text-[11px] font-extrabold tabular-nums"
          style={{ background: iconBg, color: iconColor }}
          data-testid={`insight-${testid}-count`}
        >
          {items.length}
        </span>
      </div>

      <ul className="space-y-1.5">
        {display.map((it) => (
          <li
            key={it.name_key}
            onClick={() => onOpen(it)}
            className="cursor-pointer rounded-xl py-2 px-2.5 hover:bg-slate-50 transition-colors flex items-center gap-3"
            data-testid={`insight-${testid}-row-${it.name_key}`}
          >
            <div className="flex-1 min-w-0">
              <div className="font-bold text-[13px] text-slate-900 truncate">{it.name}</div>
              <div className="text-[11px] text-slate-500 font-semibold leading-tight">
                {renderItemMeta(it)}
              </div>
            </div>
            <ActionButtons item={it} onOpen={onOpen} onContact={onContact} contactingKey={contactingKey} />
          </li>
        ))}
      </ul>

      {items.length > 3 && (
        <button
          type="button"
          onClick={onToggle}
          className="mt-2 text-[11.5px] font-bold text-slate-500 hover:text-slate-700 flex items-center gap-1"
          data-testid={`insight-${testid}-toggle`}
        >
          <CaretDown size={11} weight="bold" className={expanded ? "rotate-180" : ""} />
          {expanded ? "Réduire" : `Voir les ${items.length - 3} autres`}
        </button>
      )}
    </section>
  );
}

// ===========================================================================
// Composant principal
// ===========================================================================
export default function ApporteursInsights({ searchQuery = "", onOpenApporteur, refreshTrigger = 0, onContactStamped }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contactingKey, setContactingKey] = useState(null);
  // Expansion par bloc (clé → bool)
  const [expanded, setExpanded] = useState({});

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const res = await api.get("/apporteurs/insights");
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInsights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  if (loading || !data) return null;

  // Filtre par recherche (cohérent avec la recherche globale V4.16)
  const q = _norm((searchQuery || "").trim());
  const filterByName = (arr) => (q ? arr.filter((x) => _norm(x.name).includes(q)) : arr);

  const aAppeler = filterByName(data.a_appeler || []);
  const opportunites = filterByName(data.opportunites || []);
  const enPerte = filterByName(data.en_perte || []);
  const enCroissance = filterByName(data.en_croissance || []);
  const topPotentiel = filterByName(data.top_potentiel || []);

  // Si après filtrage tout est vide ET pas de search active → masque
  // Si search active et rien ne match → on ne rend rien (l'état "no results" est géré par parent)
  const totalVisible = aAppeler.length + opportunites.length + enPerte.length + enCroissance.length + topPotentiel.length;
  if (totalVisible === 0) return null;

  const handleOpen = (item) => {
    if (onOpenApporteur) onOpenApporteur(item);
  };

  const handleContact = async (item) => {
    if (!item?.name) return;
    setContactingKey(item.name_key);
    try {
      await api.post(`/apporteurs/${encodeURIComponent(item.name)}/mark-contacted`);
      toast.success(`Contact noté pour ${item.name}`);
      // Refresh insights + remonte au parent pour rafraîchir la liste globale
      await fetchInsights();
      if (onContactStamped) onContactStamped();
    } catch {
      toast.error("Impossible de noter le contact");
    } finally {
      setContactingKey(null);
    }
  };

  const toggleExpand = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  // Helpers meta pour blocs 2/3/4/5
  const renderOpportuniteMeta = (it) => (
    <span>
      <strong className="text-slate-700">{it.category_label}</strong>
      {" · "}
      <span>{it.reason}</span>
    </span>
  );
  const renderPerteMeta = (it) => (
    <span>
      <TrendDown size={10} weight="bold" className="inline-block mr-0.5" />
      {it.reason}
    </span>
  );
  const renderCroissanceMeta = (it) => (
    <span className="text-emerald-700">
      <TrendUp size={10} weight="bold" className="inline-block mr-0.5" />
      {it.reason}
    </span>
  );
  const renderTopMeta = (it) => (
    <span>
      <strong className="text-[color:var(--cf-primary-hover)] tabular-nums">Score {it.score}/100</strong>
      {" · "}
      <span>{it.reasons.join(" + ")}</span>
    </span>
  );

  return (
    <div className="mb-4" data-testid="apporteurs-insights-root">
      {/* Bloc 1 — À appeler (HERO) */}
      <BlockACalled
        items={aAppeler}
        onOpen={handleOpen}
        onContact={handleContact}
        contactingKey={contactingKey}
        expanded={!!expanded.aAppeler}
        onToggle={() => toggleExpand("aAppeler")}
      />

      {/* Bloc 2 — Opportunités */}
      <CompactBlock
        icon={Sparkle}
        iconColor="var(--cf-accent)"
        iconBg="var(--cf-accent-soft)"
        title="Opportunités"
        items={opportunites}
        renderItemMeta={renderOpportuniteMeta}
        onOpen={handleOpen}
        onContact={handleContact}
        contactingKey={contactingKey}
        expanded={!!expanded.opportunites}
        onToggle={() => toggleExpand("opportunites")}
        testid="opportunites"
      />

      {/* Bloc 3 — En perte de vitesse */}
      <CompactBlock
        icon={TrendDown}
        iconColor="var(--cf-warning-text)"
        iconBg="var(--cf-warning-soft)"
        title="En perte de vitesse"
        items={enPerte}
        renderItemMeta={renderPerteMeta}
        onOpen={handleOpen}
        onContact={handleContact}
        contactingKey={contactingKey}
        expanded={!!expanded.enPerte}
        onToggle={() => toggleExpand("enPerte")}
        testid="en-perte"
      />

      {/* Bloc 4 — En croissance */}
      <CompactBlock
        icon={TrendUp}
        iconColor="var(--cf-success-text)"
        iconBg="var(--cf-success-soft)"
        title="En croissance"
        items={enCroissance}
        renderItemMeta={renderCroissanceMeta}
        onOpen={handleOpen}
        onContact={handleContact}
        contactingKey={contactingKey}
        expanded={!!expanded.enCroissance}
        onToggle={() => toggleExpand("enCroissance")}
        testid="en-croissance"
      />

      {/* Bloc 5 — Top potentiel */}
      <CompactBlock
        icon={CheckCircle}
        iconColor="var(--cf-primary-hover)"
        iconBg="var(--cf-primary-soft)"
        title="Top potentiel"
        items={topPotentiel}
        renderItemMeta={renderTopMeta}
        onOpen={handleOpen}
        onContact={handleContact}
        contactingKey={contactingKey}
        expanded={!!expanded.topPotentiel}
        onToggle={() => toggleExpand("topPotentiel")}
        testid="top-potentiel"
      />
    </div>
  );
}
